import React from 'react';
import { motion } from 'motion/react';

export function NeuralBackground({ showText = false }: { showText?: boolean }) {
  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center opacity-20 no-waves-hero z-0"
    >
      <svg
        viewBox="0 0 200 280"
        width="280"
        height="392"
        className="mx-auto mb-4 neon-bolt-hero hero-bolt"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="boltStopGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%"  stopColor="#ffffff" />
            <stop offset="22%" stopColor="#00f0ff" />
            <stop offset="62%" stopColor="#ff2bd6" />
            <stop offset="100%" stopColor="#8a2be2" />
          </linearGradient>
          <filter id="boltSoftBlur">
            <feGaussianBlur stdDeviation="8" />
          </filter>
          <filter id="boltSoftBlur2">
            <feGaussianBlur stdDeviation="3" />
          </filter>
        </defs>
        {/* Outermost purple halo */}
        <path
          d="M100,4 L34,150 L74,150 L46,272 L166,108 L120,108 Z"
          fill="#8a2be2"
          opacity="0.55"
          filter="url(#boltSoftBlur)"
        />
        {/* Mid magenta */}
        <path
          d="M100,4 L40,144 L78,144 L52,260 L160,108 L118,108 Z"
          fill="#ff2bd6"
          opacity="0.78"
          filter="url(#boltSoftBlur2)"
        />
        {/* Inner cyan core */}
        <path
          d="M100,8 L46,140 L82,140 L58,252 L156,108 L116,108 Z"
          fill="url(#boltStopGrad)"
          stroke="#ffffff"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        {/* Hot center spark */}
        <path
          d="M100,12 L52,134 L86,134 L62,246 L152,108 L114,108 Z"
          fill="#ffffff"
          opacity="0.35"
          filter="url(#boltSoftBlur2)"
        />
      </svg>
      {/* Compass rose — shown on theme-day + theme-ocean */}
      <svg viewBox="0 0 200 280" width="280" height="392" className="mx-auto mb-4 hero-compass hidden" aria-hidden="true">
        <circle cx="100" cy="140" r="76" fill="none" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.75" />
        <circle cx="100" cy="140" r="86" fill="none" stroke="currentColor" strokeWidth="0.8" strokeOpacity="0.35" strokeDasharray="2 5" />
        <circle cx="100" cy="140" r="62" fill="none" stroke="currentColor" strokeWidth="0.8" strokeOpacity="0.3" />
        {[0, 90, 180, 270].map((deg) => (
          <line key={deg} x1="100" y1={140 - 86} x2="100" y2={140 - 76} stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" transform={`rotate(${deg} 100 140)`} strokeOpacity="0.85" />
        ))}
        <path d="M100 56 L112 130 L100 140 L88 130 Z" fill="currentColor" fillOpacity="0.7" />
        <path d="M100 224 L112 150 L100 140 L88 150 Z" fill="currentColor" fillOpacity="0.35" />
        <path d="M184 140 L110 152 L100 140 L110 128 Z" fill="currentColor" fillOpacity="0.4" />
        <path d="M16 140 L90 152 L100 140 L90 128 Z" fill="currentColor" fillOpacity="0.4" />
        <circle cx="100" cy="140" r="6" fill="currentColor" />
        <circle cx="100" cy="140" r="2" fill="rgba(255,255,255,0.4)" />
      </svg>
      {/* Gyroscope — shown on theme-classic + theme-aurora */}
      <svg viewBox="0 0 200 280" width="280" height="392" className="mx-auto mb-4 hero-gyro hidden" aria-hidden="true">
        <circle cx="100" cy="140" r="90" fill="none" stroke="currentColor" strokeWidth="3.5" strokeOpacity="0.75" />
        <circle cx="100" cy="140" r="84" fill="none" stroke="currentColor" strokeWidth="1" strokeOpacity="0.3" />
        {Array.from({ length: 12 }).map((_, i) => {
          const angle = (i * 30 * Math.PI) / 180;
          const x1 = 100 + Math.cos(angle) * 86;
          const y1 = 140 + Math.sin(angle) * 86;
          const x2 = 100 + Math.cos(angle) * 92;
          const y2 = 140 + Math.sin(angle) * 92;
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeOpacity={i % 3 === 0 ? 0.85 : 0.4} />;
        })}
        <rect x="92" y="20" width="16" height="22" rx="2" fill="currentColor" fillOpacity="0.6" />
        <line x1="100" y1="42" x2="100" y2="50" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.6" />
        <ellipse cx="100" cy="140" rx="55" ry="22" fill="none" stroke="currentColor" strokeWidth="2" strokeOpacity="0.7" transform="rotate(35 100 140)" />
        <ellipse cx="100" cy="140" rx="55" ry="22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.45" transform="rotate(-35 100 140)" />
        <circle cx="100" cy="140" r="32" fill="currentColor" fillOpacity="0.12" stroke="currentColor" strokeWidth="2" />
        <circle cx="100" cy="140" r="24" fill="none" stroke="currentColor" strokeWidth="0.8" strokeOpacity="0.5" />
        <circle cx="100" cy="140" r="10" fill="currentColor" fillOpacity="0.85" />
        <circle cx="100" cy="140" r="3" fill="rgba(255,255,255,0.6)" />
      </svg>
      {/* Overcast Sun — shown on theme-day */}
      <svg viewBox="0 0 200 280" width="280" height="392" className="mx-auto mb-4 hero-sun" aria-hidden="true">
        <circle cx="100" cy="140" r="80" fill="currentColor" opacity="0.08" />
        <circle cx="100" cy="140" r="60" fill="currentColor" opacity="0.15" />
        {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
          <line key={deg} x1="100" y1={50} x2="100" y2={72} stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" transform={`rotate(${deg} 100 140)`} opacity="0.25" />
        ))}
        <circle cx="100" cy="140" r="42" fill="currentColor" opacity="0.2" />
        <circle cx="100" cy="140" r="32" fill="currentColor" opacity="0.4" />
        <circle cx="100" cy="140" r="22" fill="currentColor" opacity="0.65" />
        <circle cx="100" cy="140" r="14" fill="currentColor" />
      </svg>
      {showText && (
        <div className="text-center mt-4">
          <p className="text-xs font-bold uppercase tracking-widest text-white">No neural waves detected</p>
          <p className="text-[10px] mt-2 tracking-widest text-white/50">Start the broadcast</p>
        </div>
      )}
    </motion.div>
  );
}
